import React, { useState, useEffect } from 'react';

const ScriptAppelAdmin = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [scriptType, setScriptType] = useState('standard');

  useEffect(() => {
    setLoading(true);
    loadScript();
  }, [scriptType]);

  const loadScript = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/script-appel?type=${scriptType}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.length === 0) {
          // Initialiser le script par défaut si vide
          await initializeScript();
        } else {
          setSections(data);
        }
      }
    } catch (error) {
      console.error('Erreur chargement script:', error);
      setMessage({ type: 'error', text: 'Erreur lors du chargement du script' });
    } finally {
      setLoading(false);
    }
  };

  const initializeScript = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/script-appel/init?type=${scriptType}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSections(data.sections);
        setMessage({ type: 'success', text: 'Script initialisé avec succès' });
      }
    } catch (error) {
      console.error('Erreur initialisation script:', error);
    }
  };

  const startEditing = (section) => {
    setEditingSection(section.section_key);
    setEditContent(section.content);
    setEditTitle(section.section_title);
  };

  const cancelEditing = () => {
    setEditingSection(null);
    setEditContent('');
    setEditTitle('');
  };

  const saveSection = async (sectionKey) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/script-appel/${sectionKey}?type=${scriptType}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          section_title: editTitle,
          content: editContent
        })
      });

      if (response.ok) {
        const updatedSection = await response.json();
        setSections(sections.map(s =>
          s.section_key === sectionKey ? updatedSection : s
        ));
        setEditingSection(null);
        setEditContent('');
        setEditTitle('');
        setMessage({ type: 'success', text: 'Section mise à jour avec succès' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        setMessage({ type: 'error', text: 'Erreur lors de la sauvegarde' });
      }
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      setMessage({ type: 'error', text: 'Erreur lors de la sauvegarde' });
    } finally {
      setSaving(false);
    }
  };

  const downloadPDF = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/script-appel/pdf?type=${scriptType}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'script-appel-eshops-pro.pdf';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        setMessage({ type: 'success', text: 'PDF téléchargé avec succès' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        setMessage({ type: 'error', text: 'Erreur lors du téléchargement du PDF' });
      }
    } catch (error) {
      console.error('Erreur téléchargement PDF:', error);
      setMessage({ type: 'error', text: 'Erreur lors du téléchargement du PDF' });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="script-admin-container">
        <div className="loading">Chargement du script...</div>
      </div>
    );
  }

  return (
    <div className="script-admin-container">
      <style>{`
        .script-admin-container {
          padding: 20px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .script-admin-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
          flex-wrap: wrap;
          gap: 15px;
        }

        .script-admin-header h2 {
          color: white;
          margin: 0;
          font-size: 1.8rem;
        }

        .script-admin-header p {
          color: rgba(255,255,255,0.7);
          margin: 5px 0 0 0;
        }

        .message-banner {
          padding: 15px 20px;
          border-radius: 8px;
          margin-bottom: 20px;
          font-weight: 500;
        }

        .message-banner.success {
          background: rgba(72, 187, 120, 0.2);
          color: #48bb78;
          border: 1px solid rgba(72, 187, 120, 0.3);
        }

        .message-banner.error {
          background: rgba(245, 101, 101, 0.2);
          color: #f56565;
          border: 1px solid rgba(245, 101, 101, 0.3);
        }

        .script-sections {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .script-section-card {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          overflow: hidden;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px 20px;
          background: rgba(102, 126, 234, 0.2);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .section-header h3 {
          color: white;
          margin: 0;
          font-size: 1.1rem;
        }

        .section-meta {
          color: rgba(255,255,255,0.5);
          font-size: 0.8rem;
        }

        .section-actions {
          display: flex;
          gap: 10px;
        }

        .btn-edit {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s;
        }

        .btn-edit:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }

        .section-content {
          padding: 20px;
        }

        .section-content pre {
          white-space: pre-wrap;
          word-wrap: break-word;
          color: rgba(255,255,255,0.9);
          font-family: inherit;
          margin: 0;
          line-height: 1.6;
          font-size: 0.95rem;
        }

        .edit-form {
          padding: 20px;
        }

        .edit-form label {
          display: block;
          color: rgba(255,255,255,0.8);
          margin-bottom: 8px;
          font-weight: 500;
        }

        .edit-form input {
          width: 100%;
          padding: 12px;
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.1);
          color: white;
          font-size: 1rem;
          margin-bottom: 15px;
        }

        .edit-form input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.2);
        }

        .edit-form textarea {
          width: 100%;
          min-height: 300px;
          padding: 15px;
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.1);
          color: white;
          font-size: 0.95rem;
          line-height: 1.6;
          resize: vertical;
          font-family: inherit;
        }

        .edit-form textarea:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.2);
        }

        .edit-buttons {
          display: flex;
          gap: 10px;
          margin-top: 15px;
        }

        .btn-save {
          background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.2s;
        }

        .btn-save:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(72, 187, 120, 0.4);
        }

        .btn-save:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-cancel {
          background: rgba(255, 255, 255, 0.1);
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.2);
          padding: 12px 24px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s;
        }

        .btn-cancel:hover {
          background: rgba(255, 255, 255, 0.2);
        }

        .loading {
          text-align: center;
          padding: 40px;
          color: rgba(255,255,255,0.7);
          font-size: 1.1rem;
        }

        .init-button {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.2s;
        }

        .init-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }

        @media (max-width: 768px) {
          .script-admin-container {
            padding: 15px;
          }

          .section-header {
            flex-direction: column;
            gap: 10px;
            text-align: center;
          }

          .edit-buttons {
            flex-direction: column;
          }
        }
      `}</style>

      <div className="script-admin-header">
        <div>
          <h2>Script d'Appel - Administration</h2>
          <p>Modifiez le contenu du script visible par les agents{scriptType === 'cee' ? ' CEE' : ' classiques'}</p>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'inline-flex', border: '1px solid #ccc', borderRadius: '8px', overflow: 'hidden' }}>
            <button
              onClick={() => setScriptType('standard')}
              style={{ padding: '8px 14px', border: 'none', cursor: 'pointer', background: scriptType === 'standard' ? '#667eea' : '#fff', color: scriptType === 'standard' ? '#fff' : '#333', fontWeight: 600 }}
            >
              Standard
            </button>
            <button
              onClick={() => setScriptType('cee')}
              style={{ padding: '8px 14px', border: 'none', cursor: 'pointer', background: scriptType === 'cee' ? '#7b2ff7' : '#fff', color: scriptType === 'cee' ? '#fff' : '#333', fontWeight: 600 }}
            >
              CEE
            </button>
          </div>
          {sections.length > 0 && (
            <button className="init-button" onClick={downloadPDF} style={{ background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)' }}>
              📄 Télécharger PDF
            </button>
          )}
          {sections.length === 0 && (
            <button className="init-button" onClick={initializeScript}>
              Initialiser le script par défaut
            </button>
          )}
        </div>
      </div>

      {message.text && (
        <div className={`message-banner ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="script-sections">
        {sections.map((section) => (
          <div key={section.section_key} className="script-section-card">
            <div className="section-header">
              <div>
                <h3>{section.section_title}</h3>
                {section.updated_at && (
                  <div className="section-meta">
                    Dernière modification: {formatDate(section.updated_at)}
                    {section.updated_by_nom && ` par ${section.updated_by_nom}`}
                  </div>
                )}
              </div>
              {editingSection !== section.section_key && (
                <div className="section-actions">
                  <button
                    className="btn-edit"
                    onClick={() => startEditing(section)}
                  >
                    Modifier
                  </button>
                </div>
              )}
            </div>

            {editingSection === section.section_key ? (
              <div className="edit-form">
                <label>Titre de la section</label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  placeholder="Titre de la section"
                />

                <label>Contenu</label>
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  placeholder="Contenu de la section..."
                />

                <div className="edit-buttons">
                  <button
                    className="btn-save"
                    onClick={() => saveSection(section.section_key)}
                    disabled={saving}
                  >
                    {saving ? 'Enregistrement...' : 'Enregistrer'}
                  </button>
                  <button
                    className="btn-cancel"
                    onClick={cancelEditing}
                    disabled={saving}
                  >
                    Annuler
                  </button>
                </div>
              </div>
            ) : (
              <div className="section-content">
                <pre>{section.content}</pre>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ScriptAppelAdmin;
